import angular from 'angular'
import moment from 'moment'

import templateUrl from './rogers-employee-sales-report.component.html'

/**
 * An employee sales report that is customized to display data for Rogers work orders.
 */
export const RogersEmployeeSalesReportComponent = {
    templateUrl,
    controllerAs: "vm",
    bindings: {
        reportType: "<",
    },
    controller: class RogersEmployeeSalesReport {

        /**
         * Create a new EmployeeSalesReport controller.
         * @param {IQService} $q An instance of the Angular $q service.
         * @param {string} apexApiUrl The root URL for the Apex API.
         * @param {RogersPayoutReportService} rogersPayoutReportService
         * @param {any} utilityService A service providing common utilities.
         * @param {any} payrollService
         * @param {any} divisionService
         */
        constructor($q, apexApiUrl, rogersPayoutReportService, utilityService,
            employeeService, payrollService, divisionService) {
            'ngInject'
            this.$q = $q
            this.apexApiUrl = apexApiUrl
            this.divisionService = divisionService;
            this.rogersPayoutReportService = rogersPayoutReportService;
            this.employeeService = employeeService;
            this.loaded = false
            this.utilityService = utilityService
            this.newHiresPageNumber = 1; // The page number for the pagination.
            this.payrollService = payrollService;
            this.payrolls = [];
            this.pageNumber = 1;
            this.pageSize = 10;
            this.selectedPayroll;
            this.filters = {};
            this.employeeCommisionXlsxUrl = apexApiUrl + "reports/rogers/employee-commission-statement.xlsx";
            this.employeeSalesReportXlsxUrl = apexApiUrl + "reports/rogers/owner-hybrid-payout-report.xlsx";

        }

        /**
         * Initialize the component.
         */
        $onInit() {
            this.isSalesReport = this.reportType === "SalesReport";

            const loaded = this.employeeService.retrieveIdentity()
                .then(identity => {
                    this.identity = identity;
                    this.employeeID = identity.EmployeeID;
                })
                .then(() => this.loadPayrolls())
                .then(() => {
                    return this.loadWorkOrderPayouts()
                });

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(loaded, "Initial load failed")
            )
                .then(() => this.loaded = true);
        }

        loadPayrolls() {
            // Hide unlocked payrolls in the dropdown on the commission statement.
            const filterLocked = !this.isSalesReport ? true : null
            return this.payrollService.retrievePayPeriods(this.employeeID, null, filterLocked)
                .then(employeePayrolls => {
                    this.payrolls = employeePayrolls.filter(payroll => {
                        return payroll.DivisionCountries.filter(country => {
                            return country.DivisionID === this.divisionService.divisionIDs.Rogers
                        })
                    });

                    this.payrolls = this.payrolls.sort(function (a, b) {
                        if (a.StartDate < b.StartDate) {
                            return 1;
                        } else if (a.StartDate > b.StartDate) {
                            return -1;
                        }
                        if (a.EndDate < b.EndDate) {
                            return 1;
                        }
                        if (a.EndDate < b.EndDate) {
                            return -1;
                        }
                        return 0;
                    });
                    this.selectedPayroll = this.selectDefaultPayroll(this.payrolls);
                    return this.payrolls;
                })
        }

        onChangePayroll() {
            this.utilityService.showLoading();
            this.loaded = false;
            this.loadWorkOrderPayouts()
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.loaded = true;
                    this.utilityService.hideLoading();
                });
        }

        loadWorkOrderPayouts() {
            const query = {
                DivisionID: this.divisionService.divisionIDs.Rogers,
                EmployeeID: this.employeeID,
                ShowLoggedInUserWorkOrders: true
            }
            let payoutReport
            if (this.isSalesReport) {
                Object.assign(query, {
                    // Filter by order date between the payroll dates.
                    StatusName: "Order Date", //TODO : Needs to be confirmed
                    StartDate: this.selectedPayroll.StartDate,
                    EndDate: this.selectedPayroll.EndDate,
                })
                payoutReport = this.rogersPayoutReportService.retrieveRogersOwnerHybridPayoutReport(query)
            } else {
                // Display the commission statement, which only includes posted work orders on the payroll.
                Object.assign(query, {
                    PayrollID: this.selectedPayroll.PayrollID
                })
                payoutReport = this.rogersPayoutReportService.retrieveRogersEmployeeCommissionStatement(query)
            }

            return payoutReport
                .then(workOrders => {
                    this.workOrderPayouts = workOrders;
                    this.filteredWorkOrderPayouts = angular.copy(this.workOrderPayouts)
                    this.calculateTotals();
                });
        }

        selectDefaultPayroll(payrolls) {
            return payrolls.reduce(function (selectedPayroll, payroll) {
                if (selectedPayroll.selected) {
                    return selectedPayroll;
                }
                const hasUnlockedPriority = !payroll.Locked && selectedPayroll.candidate.Locked;
                const isCloserToCurrentRotation = !payroll.Locked && Math.abs(moment().diff(payroll.EndDate, "day")) < Math.abs(moment().diff(selectedPayroll.candidate.EndDate, "days"));

                if (hasUnlockedPriority || isCloserToCurrentRotation) {
                    selectedPayroll.candidate = payroll;
                }

                return selectedPayroll;
            }, { selected: false, candidate: payrolls[0] || null }).candidate;
        }


        onChangeFilter() {
            this.filteredWorkOrderPayouts = angular.copy(this.workOrderPayouts);
            this.filteredWorkOrderPayouts = this.filteredWorkOrderPayouts
                .filter(payout => this.filterWorkOrderPayout(payout));
            this.calculateTotals();
        }

        onExport() {
            const params = {
                StartDate: this.isSalesReport ? kendo.format("{0:yyyy-MM-dd}", this.selectedPayroll.StartDate) : null,
                EndDate: this.isSalesReport ? kendo.format("{0:yyyy-MM-dd}", this.selectedPayroll.EndDate) : null,
                PayrollID: this.isSalesReport ? null : this.selectedPayroll.PayrollID,
                EmployeeID: this.employeeID,
                ShowLoggedInUserWorkOrders: true,
                Status: this.isSalesReport ? null : [2],
                DivisionID: this.divisionService.divisionIDs.Rogers,
                StatusName: this.isSalesReport ? "Order Date" : null //TODO : Needs to be confirmed
            }
            const queryString = this.utilityService.queryStringify(params);
            const url = this.isSalesReport ? this.employeeSalesReportXlsxUrl : this.employeeCommisionXlsxUrl;
            window.location.href = url + queryString;
        }

        filterWorkOrderPayout(payout) {
            let employeeFullName = "";
            if (payout.Employee) {
                employeeFullName = payout.Employee.FirstName + " " + payout.Employee.LastName;
            }
            return (!this.filters.EmployeeName || this.utilityService.containsInvariant(employeeFullName,
                this.filters.EmployeeName))
                && (!this.filters.StatusName || this.utilityService.containsInvariant(payout.Status,
                    this.filters.StatusName))
                && (!this.filters.SgiID || this.utilityService.containsInvariant(payout.SgiID, this.filters.SgiID))
                && (!this.filters.OrderDate || moment(this.filters.OrderDate).isSame(payout.OrderDate))
                && (!this.filters.InstalledDate || moment(this.filters.InstalledDate).isSame(payout.ConfirmedDate))
                && (!this.filters.AppointmentDate || moment(this.filters.AppointmentDate).isSame(
                    payout.AppointmentDate))
                && (!this.filters.CancelledDate || moment(this.filters.CancelledDate).isSame(payout.CancelledDate))
                && (!this.filters.DisconnectedDate || moment(this.filters.DisconnectedDate).isSame(
                    payout.DisconnectedDate))
                && (!this.filters.PayWeekEndDate || moment(this.filters.PayWeekEndDate).isSame(payout.PayWeekEndDate))
                && (!this.filters.OriginalConnectDate || moment(this.filters.OriginalConnectDate).isSame(
                    payout.OriginalConnectDate))
                && (!this.filters.AccountNumber || this.utilityService.containsInvariant(payout.AccountNumber,
                    this.filters.AccountNumber))
                && (!this.filters.CustomerFirstName || this.utilityService.containsInvariant(payout.CustomerFirstName,
                    this.filters.CustomerFirstName))
                && (!this.filters.CustomerLastName || this.utilityService.containsInvariant(payout.CustomerLastName,
                    this.filters.CustomerLastName))
                && (!this.filters.Address || this.utilityService.containsInvariant(payout.Address,
                    this.filters.Address))
                && (!this.filters.MapAreaCode || this.utilityService.containsInvariant(payout.MapAreaCode,
                    this.filters.MapAreaCode))
                && (!this.filters.ProductLine || this.utilityService.containsInvariant(payout.ProductLineName,
                    this.filters.ProductLine))
                && (!this.filters.TypeOfPayout || this.utilityService.containsInvariant(payout.TypeOfPayout,
                    this.filters.TypeOfPayout))
                && (!this.filters.ProductCode || this.utilityService.containsInvariant(payout.ProductCode,
                    this.filters.ProductCode))
                && (!this.filters.ProductName || this.utilityService.containsInvariant(payout.ProductName,
                    this.filters.ProductName))
                && (!this.filters.ProductDescription || this.utilityService.containsInvariant(
                    payout.ProductDescription, this.filters.ProductDescription))
                && (!this.filters.ClientName || this.utilityService.containsInvariant(payout.ClientName,
                    this.filters.ClientName))
                && (!this.filters.Address || this.utilityService.containsInvariant(payout.Address,
                    this.filters.Address))
                && (!this.filters.WorkOrderNumber || this.utilityService.containsInvariant(
                    payout.WorkOrderNumber, this.filters.WorkOrderNumber))
                && (!this.filters.OwnerName || this.utilityService.containsInvariant(payout.OwnerName,
                    this.filters.OwnerName))
                && (this.isSalesReport || payout.RepTotalAmount !== 0);
        }

        calculateTotals() {
            const baseTotal = {
                OfficeBaseAmount: 0,
                OfficeMultiplayAmount: 0,
                OfficeContentAmount: 0,
                OfficeInnovageRogersAdjustmentAmount: 0,
                OfficeTotalAmount: 0,
                RepBaseAmount: 0,
                RepMultiplayAmount: 0,
                RepContentAmount: 0,
                RepInnovageRogersAdjustmentAmount : 0,
                RepTotalAmount: 0
            }
            this.workOrderTotals = this.filteredWorkOrderPayouts.reduce(function (totals, payout) {
                totals.OfficeBaseAmount += payout.OfficeBaseAmount;
                totals.OfficeMultiplayAmount += payout.OfficeMultiplayAmount;
                totals.OfficeContentAmount += payout.OfficeContentAmount;
                totals.OfficeInnovageRogersAdjustmentAmoun += payout.OfficeInnovageRogersAdjustmentAmoun;
                totals.OfficeTotalAmount += payout.OfficeTotalAmount;
                totals.RepBaseAmount += payout.RepBaseAmount;
                totals.RepMultiplayAmount += payout.RepMultiplayAmount;
                totals.RepContentAmount += payout.RepContentAmount;
                totals.RepInnovageRogersAdjustmentAmount += payout.RepInnovageRogersAdjustmentAmount;
                totals.RepTotalAmount += payout.RepTotalAmount;

                return totals;
            }, baseTotal);
        }

    }
}
